import { useEffect, useRef, useState, createContext, useContext, useCallback, useMemo } from 'react';
import { useImmer } from 'use-immer';
import { createPortal } from 'react-dom';
import canvas_confetti from 'canvas-confetti'
import { Icon } from '@/components/Icon';
import { Button } from '@/form/FormComponents';
import { useLocation } from 'react-router';
import { useBlocker } from '@/systems/Blocker';
import { Scrollable, CircularMenuButton } from '@/components/DashboardComponents';

const ModalContext = createContext();

export const ModalProvider = ({ children }) => {
  const [modals, updateModals] = useImmer([]);
  const is_modal_open = modals.filter(modal => !modal.closed).length > 0;
  const location = useLocation();
  const blocker = useBlocker({ id: "modal", type: "modals", priority: 5, callback: useCallback(() => {

    let unclosed_modals = modals.filter(modal => !modal.closed);
    if (unclosed_modals.length > 0) {
      
      let topmost_unclosed_modal_index = 0;

      for (let i = modals.length - 1; i >= 0; i--) {
        if (!modals[i].closed) {
          topmost_unclosed_modal_index = i;
          break;
        }
      }

      updateModals(draft => {
        draft[topmost_unclosed_modal_index].closed = true;
      });

      // Keep blocking if there are still modals open
      if (unclosed_modals.length > 1) {
        return true;
      }

    }
    
  }, [modals, updateModals])});

  const openModal = useCallback(({ modalId, context, modalFn, pathnames = [] }) => {
    updateModals(draft => {
      draft.push({ modalId, context, modalFn, pathnames: [location.pathname].concat(pathnames), closed: false, loading: false });
    });

    blocker.block();
  }, [blocker, location.pathname, updateModals]);

  const refreshModal = useCallback(({ modalId, modalFn, reset_loading = false }) => {
    updateModals(draft => {
      let matching_modal = draft.find(modal => modal.modalId == modalId && !modal.closed);
      if (matching_modal) {
        matching_modal.modalFn = modalFn;
        if (reset_loading) {
          matching_modal.loading = false;
        }
      }
    });
  }, [updateModals]);

  // Close all different page modals when the page changes
  useEffect(() => {
    updateModals(draft => {
      draft.forEach(function(modal, index) {
        if (!modal.pathnames.includes(location.pathname)) {
          draft[index].closed = true;
        }
      });

      if (draft.filter(modal => !modal.closed).length == 0) {
        blocker.unblock();
      }
    });
  }, [location.pathname, updateModals]);

  // Clean up modals only if all entries are closed after 0.5s
  // (Avoids changing the index of modals that are still open)
  useEffect(() => {
    let timeout = setTimeout(() => {
      updateModals(draft => {
        if (draft.filter(modal => !modal.closed).length == 0) {
          draft.splice(0, draft.length);
        }
      });
    }, 500);

    return () => {
      clearTimeout(timeout);
    }
  }, [modals, updateModals]);

  const provider_value = useMemo(() => ({ openModal, refreshModal, is_modal_open }), [openModal, refreshModal, is_modal_open]);

  return (
    <>
      <ModalContext.Provider value={provider_value}>
        {children}
      </ModalContext.Provider>
      {modals.map(function ({ context, modalFn, loading, closed }, index) {
        const is_open_modal_above = modals.filter((modal, i) => i > index && !modal.closed).length > 0;
        const is_open_modal_below = modals.filter((modal, i) => i < index && !modal.closed).length > 0;

        function closeModal() {
          if (modals.filter((modal, i) => i != index && !modal.closed).length == 0) {
            blocker.unblock();
          }

          updateModals(draft => {
            if (draft[index]) {
              draft[index].closed = true;
            }
          });
        }

        function setLoading(loading) {
          updateModals(draft => {
            draft[index].loading = loading;
          });
        }

        return (
          <ModalContext.Provider key={index} value={{ openModal, refreshModal, is_open_modal_above, is_open_modal_below, open: closed ? false : (loading ? "loading" : true), handleClose: closeModal }}>
            {modalFn({ context, closeModal, setLoading })}
          </ModalContext.Provider>
        );
      })}
    </>
  );
};

// modalFn should return a Modal component. It's passed an object with the following properties:
// context: the context passed to openModal
// closeModal: a function to close the modal. Should be called before any navigation.
// setLoading: a function to set the loading state of the modal
export function useModal(modalFn, dependencies = [], reset_loading_when_dependencies_change = false) {
  const { refreshModal, openModal } = useContext(ModalContext) || {}; // I did this instead of destructuring because it kept erroring in the hot reload anytime I made any changes due to this being undefined
  const [modalId] = useState(Math.random().toString(36).substring(7));
  const modalFnRef = useRef(null);

  useEffect(() => { // ?. is just to fix hot reload issue because this is undefined
    refreshModal?.({ modalId, modalFn: modalFnRef.current, reset_loading: reset_loading_when_dependencies_change });
  }, [...dependencies]);

  const openModalWithContext = useCallback((context, { pathnames } = {}) => {
    openModal({ modalId, context, modalFn: modalFnRef.current, pathnames });
  }, [openModal, modalId]);

  modalFnRef.current = modalFn;

  return useMemo(() => [openModalWithContext], [openModalWithContext]);
}

export function useIsModalOpen() {
  const { is_modal_open } = useContext(ModalContext) || {};
  return is_modal_open;
}

export function Modal({ open, onClose, title, onBack, width, full_screen = false, confetti, image = false, children }) {
  const modalContext = useContext(ModalContext);
  if (modalContext && modalContext.open != undefined) {
    open = modalContext.open;
    onClose = modalContext.handleClose;
  }
  const is_open_modal_above = modalContext?.is_open_modal_above;
  const is_open_modal_below = modalContext?.is_open_modal_below;

  const dialogRef = useRef(null);

  const [was_open, setWasOpen] = useState(false);
  const [was_shown, setWasShown] = useState(false);
  const [is_open_or_closing, setIsOpenOrClosing] = useState(false);
  const [lastDisplayKey, setlastDisplayKey] = useState(0);

  useEffect(() => {
    const dialog = dialogRef.current;
    let closing_timeout;

    if (open) {
      setWasOpen(open);
    }

    if (open) {
      if (open !== "loading" && was_open !== "loading") {
        setlastDisplayKey(Date.now());
        setIsOpenOrClosing(true);
      }
    } else {
      if (was_open == "loading") {
        setIsOpenOrClosing("loading");
      }
      setWasOpen(false);
      if (dialog && dialog.open) {
        dialog.close();
      }
      closing_timeout = setTimeout(() => {
        setIsOpenOrClosing(false);
      }, 200);
      return () => {
        clearTimeout(closing_timeout);
      };
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (was_open) {
      if (dialog.showModal) {
        dialog.showModal();
        setWasShown(true);
      }

      const handleClose = () => {
        onClose();
      };

      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          if (event.target.closest(".markdown_editor")) {
            event.preventDefault();
          }
        }
      };

      dialog.addEventListener('close', handleClose);
      dialog.addEventListener('keydown', handleKeyDown);

      return () => {
        dialog.removeEventListener('close', handleClose);
        dialog.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [was_open != false]);

  const canvas_ref = useRef(null);

  useEffect(() => {
    if (open && confetti) {
      let confetti_instance = canvas_confetti.create(canvas_ref.current, { resize: true });
      let colors = ['#f44336', '#2196f3', '#4caf50', '#fdd835', '#9c27b0', '#ff9800'];

      setTimeout(function() {
        confetti_instance({
          particleCount: 180,
          angle: 90,
          gravity: 0.8,
          spread: 360,
          startVelocity: 30,
          origin: { y: 0.45 },
          colors: colors
        });
        confetti_instance({
          particleCount: 50,
          spread: 360,
          startVelocity: 9,
          origin: { y: 0.45 },
          gravity: 0.8,
          colors: colors
        });
        confetti_instance({
          particleCount: 10,
          spread: 360,
          startVelocity: 5,
          origin: { y: 0.45 },
          gravity: 0.8,
          colors: colors
        });
      }, 40)
    }
  }, [open]);

  return createPortal(
    <>
      {((open || is_open_or_closing) &&
        <>
          <dialog ref={dialogRef} className={was_shown ? "was_shown" : ""}>
            <div className={"modal_background" + (image ? " image_modal_background" : "") + (open ? " modal_background_in" : " modal_background_out") + (is_open_modal_above ? " is_open_modal_above" : (is_open_modal_below ? " is_open_modal_below" : ""))} onClick={onClose}></div>
            <Scrollable className={"modal_dialog" + (image ? " image_modal" : "") + (full_screen ? " full_screen_modal" : "") + (open ? (" modalIn"+(is_open_modal_below ? " is_open_modal_below" : "")) : " modalOut") + ((open == "loading" || was_open == "loading" || is_open_or_closing == "loading") ? " disabled" : "")} style={{width: width || null}} inert={open !== true ? "true" : undefined} depencencies={[children, open, was_shown, is_open_or_closing]}>
              <div className="indeterminate_container"><div className="indeterminate"><div></div></div></div>
              {title ? <div className="modal_title"><div>{onBack ? <CircularMenuButton onClick={onBack} icon="arrow-left"/> : null}<div>{title}</div></div></div> : null}
              <div style={{display: "contents"}} key={lastDisplayKey}>
                {children}
              </div>
            </Scrollable>
            {confetti ? <canvas className={"modal_confetti"} ref={canvas_ref}></canvas> : null}
            {(image && open) ? <div 
              className="close_modal_button" 
              onClick={onClose}
              tabIndex={0}
              aria-label="Close"
            >
              <Icon name="arrow-left"></Icon>
            </div> : null}
          </dialog>
        </>
      )}
    </>,
    document.body
  );
}

export function ModalContent({children}) {
  return (
    <div className="modal_content nearest_scrollable_container">
      {children}
    </div>
  )
}

export function ModalButtons({centered, children}) {
  return (
    <div className={`modal_buttons ${centered ? "centered" : ""}`}>
      <div className="buttons">
        {children}
      </div>
    </div>
  )
}

export function ModalListItem({icon, text, onClick}) {
  return (
    <div className="modal_list_item" onClick={onClick} tabIndex={0}>
      <Icon name={icon}/>
      <div className="text">{text}</div>
    </div>
  )
}

export function useErrorModal() {
  return useModal(({ context, closeModal }) => {
    return (
      <Modal title="Something went wrong">
        <ModalContent>
          Please try again. If the issue persists, please contact support.
        </ModalContent>
        <ModalButtons>
          <Button text="Dismiss" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  });
}

export function useHelpModal() {
  return useModal(({ context, closeModal }) => {
    return (
      <Modal title="Help and support">
        <ModalContent>
          We're here to help! Please contact us at <a href="mailto:support@sapientclinician.com" className="links">support@sapientclinician.com</a> for assistance.
        </ModalContent>
        <ModalButtons>
          <Button text="Dismiss" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  });
}